import { WorkShift, QueryParams } from "@/sharedTypes";
import { baseApi } from "../baseApi";
import { flattenQueryParams } from "@/utils/requestUtils";

const workShiftTag = "WorkShift";

export const workShiftApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getWorkShifts: builder.query<any, Record<string, any> | void>({
      query: (params) => {
        const queryString = params
          ? `?${new URLSearchParams(flattenQueryParams(params as QueryParams)).toString()}`
          : "";
        return `/shift-hours${queryString}`;
      },
      providesTags: (result) =>
        result
          ? [
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ...((result.items || result.data || result || []).map(({ id }: any) => ({
                type: workShiftTag,
                id,
              }))),
              { type: workShiftTag, id: "LIST" },
            ]
          : [{ type: workShiftTag, id: "LIST" }],
    }),
    getWorkShiftById: builder.query<WorkShift, string>({
      query: (id) => `/shift-hours/${id}`,
      transformResponse: (response: any) => response?.data ?? response,
      providesTags: (_result, _error, id) => [{ type: workShiftTag, id }],
    }),
    createWorkShift: builder.mutation<WorkShift, Partial<WorkShift>>({
      query: (body) => ({
        url: `/shift-hours`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: workShiftTag, id: "LIST" }],
    }),
    updateWorkShift: builder.mutation<WorkShift, { id: number; data: Partial<WorkShift> }>({
      query: ({ id, data }) => ({
        url: `/shift-hours/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: workShiftTag, id },
        { type: workShiftTag, id: "LIST" },
      ],
    }),
    deleteWorkShift: builder.mutation<void, number>({
      query: (id) => ({
        url: `/shift-hours/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: workShiftTag, id },
        { type: workShiftTag, id: "LIST" },
      ],
    }),
    restoreWorkShift: builder.mutation<void, number>({
      query: (id) => ({
        url: `/shift-hours/${id}/restore`,
        method: "PUT",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: workShiftTag, id },
        { type: workShiftTag, id: "LIST" },
      ],
    }),
    hardDeleteWorkShift: builder.mutation<void, number>({
      query: (id) => ({
        url: `/shift-hours/${id}/permanent`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: workShiftTag, id },
        { type: workShiftTag, id: "LIST" },
      ],
    }),
    getShiftAgents: builder.query<any, { shiftId: number; params?: Record<string, any> }>({
      query: ({ shiftId, params }) => {
        const queryString = params
          ? `?${new URLSearchParams(flattenQueryParams(params as QueryParams)).toString()}`
          : "";
        return `/shift-hours/${shiftId}/agents${queryString}`;
      },
      providesTags: (_result, _error, { shiftId }) => [{ type: workShiftTag, id: shiftId }],
    }),
    assignShiftToAgents: builder.mutation<any, { agent_ids: number[]; shift_id: number }>({
      query: (body) => ({
        url: `/agents/assign-multiple-shift`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { shift_id }) => [{ type: workShiftTag, id: shift_id }],
    }),
    removeAgentFromShift: builder.mutation<void, { agentId: number; shiftId: number }>({
      query: ({ agentId }) => ({
        url: `/agents/${agentId}`,
        method: "PUT",
        body: { shift_id: null },
      }),
      invalidatesTags: (_result, _error, { shiftId }) => [{ type: workShiftTag, id: shiftId }],
    }),
  }),
});

export const {
  useGetWorkShiftsQuery,
  useGetWorkShiftByIdQuery,
  useCreateWorkShiftMutation,
  useUpdateWorkShiftMutation,
  useDeleteWorkShiftMutation,
  useRestoreWorkShiftMutation,
  useHardDeleteWorkShiftMutation,
  useGetShiftAgentsQuery,
  useAssignShiftToAgentsMutation,
  useRemoveAgentFromShiftMutation,
} = workShiftApi;
